import { useCallback, useEffect, useState } from 'react';
import type { SavedTravellerView } from '@busbook/shared';
import { api } from './api.js';

/**
 * The people this customer books for.
 *
 * Loads once and keeps the list in state, because it is short by nature — a passenger has
 * a handful of family members, not a directory — and refetching it after every edit would
 * be more traffic than the whole feature saves.
 *
 * Silent on failure by design: this is a convenience on top of a name field that still
 * works when typed into. A booking flow must not be blocked by a list of shortcuts.
 */
export function useTravellers(enabled: boolean): {
  travellers: SavedTravellerView[];
  loading: boolean;
  save: (input: {
    fullName: string;
    phone?: string;
    idNumber?: string;
    email?: string;
  }) => Promise<void>;
  remove: (id: string) => Promise<void>;
  reload: () => Promise<void>;
} {
  const [travellers, setTravellers] = useState<SavedTravellerView[]>([]);
  const [loading, setLoading] = useState(enabled);

  const reload = useCallback(async () => {
    if (!enabled) return;
    try {
      setTravellers(await api.get<SavedTravellerView[]>('/public/travellers'));
    } catch {
      // Signed out, or the operator has the feature off. Either way there is nothing to
      // show and nothing to say — the name field beneath still works.
      setTravellers([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = useCallback(
    async (input: { fullName: string; phone?: string; idNumber?: string; email?: string }) => {
      const saved = await api.request<SavedTravellerView>('/public/travellers', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      // Replaced rather than appended: the API answers a duplicate with the existing row,
      // so a passenger who saves the same person twice must not see them listed twice.
      setTravellers((current) => [
        saved,
        ...current.filter((t) => t.id !== saved.id),
      ].sort((a, b) => a.fullName.localeCompare(b.fullName)));
    },
    [],
  );

  const remove = useCallback(async (id: string) => {
    await api.request(`/public/travellers/${id}`, { method: 'DELETE' });
    setTravellers((current) => current.filter((t) => t.id !== id));
  }, []);

  return { travellers, loading, save, remove, reload };
}
